import React from "react";
import { useQuote } from "../context/QuoteContext";
import { formatEur } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { RotateCcw, Check, Maximize2 } from "lucide-react";
import { PricePerM3Card } from "./PricePerM3Card";
import { PriceGauge } from "./PriceGauge";
import MarketVerifySection from "./MarketVerifySection";
import PackingSection from "./PackingSection";

export default function AreaWorkArea({ embedded = false }) {
  const { areaData, setAreaData, settings, confirmArea } = useQuote();
  const upd = (patch) => setAreaData((a) => ({ ...a, ...patch }));

  const volNetto = (areaData.area || 0) * (areaData.altezza || 0) * ((areaData.riempimento || 0) / 100);
  const prezzo = volNetto * (areaData.tariffaM3 || 0);

  return (
    <div className="space-y-5 fade-in">
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-5">
          <Maximize2 className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">Calcolo per area</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Area (m²)</Label>
            <Input type="number" value={areaData.area || ""} onChange={(e) => upd({ area: parseFloat(e.target.value) || 0 })} placeholder="0" className="mt-1" data-testid="area-input" />
          </div>
          <div>
            <Label className="text-xs">Altezza soffitto (m)</Label>
            <Input type="number" step="0.1" value={areaData.altezza} onChange={(e) => upd({ altezza: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="area-altezza-input" />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between mb-2">
            <Label className="text-xs">Riempimento</Label>
            <span className="text-sm font-semibold text-[#1a5f5a]">{areaData.riempimento}%</span>
          </div>
          <Slider value={[areaData.riempimento]} min={10} max={100} step={5} onValueChange={([v]) => upd({ riempimento: v })} data-testid="area-riempimento-slider" />
        </div>

        <div className="mt-4">
          <Label className="text-xs flex items-center gap-2">Tariffa (€/m³)
            {areaData.isTariffaOverridden && (
              <button onClick={() => upd({ tariffaM3: settings.pricePerM3, isTariffaOverridden: false })} className="text-[#1a5f5a] flex items-center gap-0.5 text-[11px]"><RotateCcw className="h-3 w-3" /> reset</button>
            )}
          </Label>
          <Input type="number" value={areaData.tariffaM3} onChange={(e) => upd({ tariffaM3: parseFloat(e.target.value) || 0, isTariffaOverridden: true })} className="mt-1" data-testid="area-tariffa-input" />
        </div>
      </div>

      <div className="bg-[#1a5f5a] text-white rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">Volume netto</div>
            <div className="text-2xl font-display font-semibold mt-1" data-testid="area-volume">{volNetto.toFixed(2)} m³</div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.2em] text-white/60">Prezzo mensile</div>
            <div className="text-2xl font-display font-semibold mt-1" data-testid="area-prezzo">€{formatEur(prezzo)}</div>
          </div>
        </div>
      </div>

      <PricePerM3Card total={prezzo} volume={volNetto} showBoxCount
        note="Prezzo per m³ calcolato sul volume netto e sulla tariffa indicata." />

      <PriceGauge volume={volNetto} monthly={prezzo} />

      <PackingSection />

      <MarketVerifySection
        area={areaData.area}
        endpoint="/ai/box-rental-market"
        storeKey="boxRental"
        title="Verifica il mercato"
        description="Trova box / garage in affitto vicini alla metratura indicata (±10%) con canone mensile nella città scelta."
        testidPrefix="box-rental"
        icon="store"
      />

      <MarketVerifySection
        area={areaData.area}
        endpoint="/ai/self-storage-market"
        storeKey="selfStorage"
        title="Verifica self-storage"
        description="Trova box self-storage professionali vicini alla metratura indicata (±10%) con canone mensile nella città scelta."
        testidPrefix="self-storage-market"
        icon="warehouse"
      />

      {!embedded && (
        <Button onClick={confirmArea} disabled={!areaData.area} className="w-full bg-[#1a5f5a] hover:bg-[#134744] h-11 gap-2" data-testid="area-confirm-button">
          <Check className="h-4 w-4" /> Conferma
        </Button>
      )}
    </div>
  );
}
